import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { api } from '../services/apiClient';
import { LayoutGrid, AlertCircle, RefreshCw, Filter, Clock, Globe, Crown } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';

const INDICES = [
    { id: 'SP500', name: 'S&P 500 (États-Unis)' },
    { id: 'NASDAQ100', name: 'Nasdaq 100 (Tech US) - PRO' },
    { id: 'CAC40', name: 'CAC 40 (France) - PRO' },
    { id: 'DAX', name: 'DAX 40 (Allemagne) - PRO' },
    { id: 'FTSE100', name: 'FTSE 100 (UK) - PRO' },
    { id: 'TSX60', name: 'S&P/TSX 60 (Canada) - PRO' },
    { id: 'ASX200', name: 'ASX 200 (Australie) - PRO' }
];

const HeatmapPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlIndex = searchParams.get('index');

    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSector, setSelectedSector] = useState('ALL');
    
    // NOUVEAU : État PRO
    const [isProUser, setIsProUser] = useState(false);
    
    // Initialisation intelligente : si non-PRO, on force SP500
    const [selectedIndex, setSelectedIndex] = useState('SP500');
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        const checkProStatus = async () => {
             try {
                const token = localStorage.getItem('token');
                if (token) {
                    const userRes = await api.get('/auth/me');
                    const isPro = userRes.data.is_pro;
                    setIsProUser(isPro);
                    
                    // Une fois le statut connu, on ajuste l'index
                    const initialIndex = urlIndex || localStorage.getItem('keyforin_heatmap_index') || 'SP500';
                    if (!isPro && initialIndex !== 'SP500') {
                        setSelectedIndex('SP500'); // Force SP500 si pas PRO
                        setSearchParams({ index: 'SP500' }, { replace: true });
                    } else {
                        setSelectedIndex(initialIndex);
                    }
                }
            } catch (error) {
                console.error("Erreur Auth:", error);
                setSelectedIndex('SP500'); // Parapluie de sécurité
            }
        };
        checkProStatus();
    }, []);

    useEffect(() => {
        if (urlIndex && urlIndex !== selectedIndex && isProUser) {
            setSelectedIndex(urlIndex);
            setSelectedSector('ALL');
            localStorage.setItem('keyforin_heatmap_index', urlIndex);
        }
    }, [urlIndex, isProUser]);

    const fetchHeatmapData = async (isAuto = false, indexToFetch = selectedIndex) => {
        if (!isAuto) setLoading(true);
        setError(null);
        try {
            // Sécurité côté client : on ne fetch que le SP500 si pas PRO
            const finalIndex = isProUser ? indexToFetch : 'SP500';
            const res = await api.get(`/stocks/heatmap?index=${finalIndex}`);
            
            const processedSeries = res.data.map(sector => ({
                name: sector.name,
                data: sector.data.map(stock => {
                    let bgColor = '#94a3b8';
                    if (stock.change <= -3) bgColor = '#e11d48';
                    else if (stock.change < 0) bgColor = '#fb7185';
                    else if (stock.change >= 3) bgColor = '#10b981';
                    else if (stock.change > 0) bgColor = '#34d399';
                    
                    return { ...stock, fillColor: bgColor };
                })
            }));
            
            setSeries(processedSeries);
            setLastUpdated(new Date());
        } catch (err) {
            setError("Impossible de charger la carte thermique pour cet indice.");
            setSeries([]);
        } finally {
            setLoading(false);
        }
    };

    // Chargement initial + Auto-Refresh
    useEffect(() => {
        if (searchParams.get('index') !== selectedIndex && isProUser) {
            setSearchParams({ index: selectedIndex }, { replace: true });
        }

        fetchHeatmapData(false, selectedIndex);

        const intervalId = setInterval(() => {
            fetchHeatmapData(true, selectedIndex);
        }, 900000);

        return () => clearInterval(intervalId);
    }, [selectedIndex, setSearchParams, isProUser]);

    const handleIndexChange = (e) => {
        if (!isProUser && e.target.value !== 'SP500') {
            alert("Accès limité : La version gratuite ne permet de visualiser que le S&P 500. Passez PRO pour débloquer les marchés internationaux !");
            window.location.href = '/pricing';
            return;
        }
        const newIndex = e.target.value;
        setSelectedIndex(newIndex);
        setSelectedSector('ALL');
        localStorage.setItem('keyforin_heatmap_index', newIndex);
        setSearchParams({ index: newIndex });
    };
    
    const handleSectorChange = (e) => {
        if (!isProUser) {
             alert("Accès limité : Le filtrage sectoriel de la Heatmap est réservé aux membres PRO.");
             window.location.href = '/pricing';
             return;
        }
        setSelectedSector(e.target.value);
    }

    const sectors = ['ALL', ...Array.from(new Set(series.map(s => s.name)))].sort();
    
    const displaySeries = selectedSector === 'ALL' 
        ? series 
        : series.filter(s => s.name === selectedSector);

    const options = {
        chart: {
            type: 'treemap',
            toolbar: { show: false },
            animations: { enabled: true, speed: 600 },
            background: 'transparent',
            fontFamily: 'system-ui, -apple-system, sans-serif',
        },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontWeight: 800, colors: ['#ffffff'] },
            dropShadow: { enabled: false },
            formatter: function(text, op) {
                const dataPoint = op.w.config.series[op.seriesIndex].data[op.dataPointIndex];
                const changeStr = dataPoint.change > 0 ? `+${dataPoint.change}%` : `${dataPoint.change}%`;
                return [dataPoint.x, changeStr];
            }
        },
        stroke: { show: true, width: 2, colors: ['#ffffff'] },
        plotOptions: {
            treemap: { enableShades: false, useFillColorAsStroke: false }
        },
        tooltip: {
            theme: 'light',
            custom: function({series, seriesIndex, dataPointIndex, w}) {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                const color = data.change >= 0 ? '#10b981' : '#e11d48';
                
                let currencySymbol = '$';
                if (selectedIndex === 'CAC40' || selectedIndex === 'DAX') currencySymbol = '€';
                else if (selectedIndex === 'FTSE100') currencySymbol = '£';
                else if (selectedIndex === 'TSX60') currencySymbol = 'C$';
                else if (selectedIndex === 'ASX200') currencySymbol = 'A$';

                let mcLabel = data.y >= 1e12 ? (data.y / 1e12).toFixed(2) + 'T' : (data.y >= 1e9 ? (data.y / 1e9).toFixed(2) + 'B' : (data.y / 1e6).toFixed(2) + 'M');

                return `
                    <div style="padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                        <div style="font-size: 16px; font-weight: 900; color: #0f172a;">${data.x} <span style="font-weight: 500; font-size: 12px; color: #64748b;">${data.company}</span></div>
                        <div style="margin-top: 8px; font-size: 13px; font-weight: 600; color: #475569;">Prix : <span style="color: #0f172a;">${currencySymbol}${data.price}</span></div>
                        <div style="margin-top: 4px; font-size: 13px; font-weight: 600; color: #475569;">Variation : <span style="color: ${color};">${data.change > 0 ? '+' : ''}${data.change}%</span></div>
                        <div style="margin-top: 4px; font-size: 12px; font-weight: 500; color: #94a3b8;">Market Cap : ${currencySymbol}${mcLabel}</div>
                    </div>
                `;
            }
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-12 font-sans">
            <div className="max-w-[98%] mx-auto">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-6">
                    
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
                            <LayoutGrid size={28} className="text-indigo-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Global Heatmap</h1>
                            <div className="flex items-center gap-2 text-slate-500 text-sm font-medium mt-1">
                                <Clock size={14} />
                                <span>Auto-refresh (15m) • Actu. à {lastUpdated.toLocaleTimeString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto">
                        
                        <div className="relative w-full sm:w-64">
                            <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${isProUser ? 'text-indigo-500' : 'text-slate-400'}`}>
                                <Globe size={16} />
                            </div>
                            <select 
                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm font-black shadow-sm appearance-none cursor-pointer focus:outline-none transition-colors ${
                                    isProUser 
                                    ? 'bg-indigo-50 border border-indigo-200 text-indigo-900' 
                                    : 'bg-white border border-slate-200 text-slate-700'
                                }`}
                                value={selectedIndex}
                                onChange={handleIndexChange}
                            >
                                {INDICES.map(idx => (
                                    <option key={idx.id} value={idx.id}>
                                        {idx.name} {!isProUser && idx.id !== 'SP500' ? ' 🔒' : ''}
                                    </option>
                                ))}
                            </select>
                            {!isProUser && (
                                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none text-amber-500">
                                    <Crown size={14} />
                                </div>
                            )}
                        </div>

                        <div className="relative w-full sm:w-56">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                                <Filter size={16} />
                            </div>
                            <select 
                                className={`w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm appearance-none cursor-pointer focus:outline-none focus:border-indigo-500 transition-colors ${!isProUser ? 'text-slate-400' : 'text-slate-700'}`}
                                value={selectedSector}
                                onChange={handleSectorChange}
                            >
                                <option value="ALL">Tous les secteurs {!isProUser ? ' 🔒' : ''}</option>
                                {isProUser && sectors.filter(s => s !== 'ALL').map(sector => (
                                    <option key={sector} value={sector}>{sector}</option>
                                ))}
                            </select>
                             {!isProUser && (
                                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none text-amber-500">
                                    <Crown size={14} />
                                </div>
                            )}
                        </div>

                        <button onClick={() => fetchHeatmapData(false)} disabled={loading} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white border border-slate-200 px-4 py-2.5 rounded-xl font-bold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors shadow-sm shrink-0">
                            <RefreshCw size={16} className={loading ? 'animate-spin text-indigo-500' : ''} />
                            <span className="hidden sm:inline">Actualiser</span>
                        </button>
                        
                        {!isProUser && (
                           <Link to="/pricing" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-xl font-bold text-amber-700 hover:bg-amber-100 transition-colors shadow-sm shrink-0">
                               <Crown size={16} /> <span className="hidden sm:inline">PRO</span>
                           </Link>
                        )}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-2 shadow-sm relative" style={{ minHeight: '80vh' }}>
                    {loading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-2xl z-10">
                            <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-slate-500 font-bold">Synchronisation avec {INDICES.find(i => i.id === selectedIndex)?.name}...</p>
                        </div>
                    ) : series.length > 0 ? (
                        <Chart options={options} series={displaySeries} type="treemap" height="100%" />
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                            <AlertCircle size={48} className="mb-4 opacity-50" />
                            <p className="font-bold">Aucune donnée disponible pour cet indice actuellement.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HeatmapPage;
